import { css, styled } from '@mui/material';

import { useThrottledSubmit } from '../../utils/useThrottledSubmit';
import { Button } from '../Button';
import { resolveFooterButtonTestId } from '../footerButtonTestId';
import { Tooltip } from '../Tooltip';
import { useModalFooterContext } from './ModalFooterContext';
import { SUBMIT_EVENT_TYPES } from './types';

import type { MouseEvent } from 'react';
import type { ModalFooterButtonProps } from './types';

const ModalFooterButton = (props: ModalFooterButtonProps) => {
  const {
    children,
    'data-testid': dataTestId,
    disabled: disabledProp,
    disabledTooltip,
    eventType,
    onClick,
    variant,
    ...otherButtonProps
  } = props;
  const { message = '', ...otherTooltipProps } = disabledTooltip || {};
  const { variantMap, labelMap, loading } = useModalFooterContext();
  const throttledClick = useThrottledSubmit(onClick);

  const disabled = disabledProp || loading;
  const isSubmitEvent = !!eventType && SUBMIT_EVENT_TYPES.includes(eventType);

  // 제출 계열 버튼은 렌더 커밋 전 중복 클릭을 throttle로 막는다.
  // disabled는 렌더 파생값이라 첫 클릭이 loading을 켜도 그 리렌더가 커밋되기 전까지는
  // 버튼이 아직 활성이고, 그 사이의 두 번째 클릭이 통과한다.
  // 취소·닫기 계열은 연속 클릭을 막을 이유가 없어 그대로 통과시킨다.
  const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
    if (!isSubmitEvent) {
      onClick?.(e);

      return;
    }

    throttledClick(e);
  };

  return (
    <Tooltip message={!disabled ? '' : message} {...otherTooltipProps}>
      <span>
        <ModalFooterButtonBase
          variant={eventType && variantMap[eventType] ? variantMap[eventType] : variant}
          disabled={disabled}
          onClick={handleClick}
          size="lg"
          data-testid={dataTestId ?? resolveFooterButtonTestId(eventType)}
          {...otherButtonProps}
        >
          {eventType && labelMap[eventType]
            ? typeof labelMap[eventType] === 'function'
              ? labelMap[eventType]()
              : labelMap[eventType]
            : children}
        </ModalFooterButtonBase>
      </span>
    </Tooltip>
  );
};

export default ModalFooterButton;

const ModalFooterButtonBase = styled(Button, { name: 'ModalFooter', slot: 'button' })(() => css``);
